import { computed, shallowRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { ReportEvents, type KeyboardPerfCapability } from '@rapoo/device-sdk'
import { useDeviceStore } from '@/stores'
import { isMacOS } from '@/utils/ua'
import type { KeyPlatform } from '../types'

function sysModeToPlatform(sysMode: 0 | 1): KeyPlatform {
  return sysMode === 1 ? 'mac' : 'win'
}

function hostOsPlatform(): KeyPlatform {
  return isMacOS ? 'mac' : 'win'
}

export function useKeyboardPlatform() {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const currentPlatform = shallowRef<KeyPlatform>(hostOsPlatform())

  const supportsMacMode = computed(() => {
    const cap = currentDevice.value?.manifest.capabilities?.find(
      (capability): capability is KeyboardPerfCapability => capability.type === 'keyboardPerf',
    )
    return cap?.config.macMode === true
  })

  const applyDeviceSysMode = (sysMode: 0 | 1) => {
    if (isMacOS || !supportsMacMode.value) return
    const nextPlatform = sysModeToPlatform(sysMode)
    if (currentPlatform.value === nextPlatform) return
    currentPlatform.value = nextPlatform
  }

  /**
   * 同步 Win/Mac 层显示。
   * 优先用设备已上报的 sysMode；尚未上报时按宿主 OS 默认，避免 EEPROM 未写入时
   * getMacMode() 读到异常默认值（如恒为 Mac）导致 Windows 上只显示 Mac 层。
   * 后续由 KeyUserData / SysModeChanged 再对齐硬件真实模式。
   */
  const refreshKeyboardPlatform = async () => {
    currentPlatform.value = hostOsPlatform()

    if (isMacOS || !supportsMacMode.value) return

    const custom = currentDevice.value?.userCustomData
    const reported = custom && 'sysMode' in custom ? custom.sysMode : undefined
    if (reported === 0 || reported === 1) {
      applyDeviceSysMode(reported)
    }
  }

  watch(
    currentDevice,
    (device, _prev, onCleanup) => {
      if (!device || isMacOS) return

      const onSysModeChanged = (sysMode: 0 | 1) => {
        applyDeviceSysMode(sysMode)
      }

      // 首包只建快照不发 SysModeChanged；这里用 KeyUserData 补上首次同步
      const onKeyUserData = (userData: { sysMode?: 0 | 1 }) => {
        if (userData.sysMode === 0 || userData.sysMode === 1) {
          applyDeviceSysMode(userData.sysMode)
        }
      }

      device.on(ReportEvents.SysModeChanged, onSysModeChanged)
      device.on(ReportEvents.KeyUserData, onKeyUserData)
      onCleanup(() => {
        device.off(ReportEvents.SysModeChanged, onSysModeChanged)
        device.off(ReportEvents.KeyUserData, onKeyUserData)
      })
    },
    { immediate: true },
  )

  return {
    currentPlatform,
    refreshKeyboardPlatform,
  }
}
